import React, { useEffect, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ReactComponent as DocumentIcon } from '../../../assets/icons/document.svg';
import { ReactComponent as DocumentIconDark } from '../../../assets/icons/iconsDark/document-dark.svg';
import { ReactComponent as LocationIcon } from '../../../assets/icons/location-icon.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { ReactComponent as StationIcon } from '../../../assets/icons/sidebar/sidebar-account.svg';
import { ReactComponent as ExpandIcon } from '../../../assets/icons/tables/expand-icon.svg';
import { ReactComponent as NewTabIcon } from '../../../assets/icons/tables/new-tab.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import ThemeContext from '../../../contexts/theme-context/ThemeContext';
import {
  AvailableStation,
  RequestStationInterface,
} from '../../../interface/station-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  fetchApprovedStations,
  setApprovedStationFilterData,
  updateStationFilters,
  fetchListFilters,
  disableFiltersApplied,
  setSortData,
} from '../../../redux/slice/ApprovedStationManagementSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import { setPageTitle } from '../../../redux/slice/PageTitleSlice';
import { RootState } from '../../../redux/store';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import DeviceSidePanel from '../../../shared-components/table-side-panel/DeviceSidePanel';
import StationSidePanel from '../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../../shared-functions/DataTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { FormatUtcString } from '../../../shared-functions/FormatUtcString';
import stationApi from '../../../utils/api/station';

import AddStation from './AddStation';

/**
 * Add new station component
 */
function ApprovedList({
  renderAvailability,
}: {
  renderAvailability: (value: AvailableStation) => JSX.Element;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('stationManagement');
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const {
    stations,
    page,
    limit,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state: RootState) => state.approvedStation);
  const { currentFacilityTagsList } = useAppSelector((state) => state.facilityTags);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [currentRowData, setCurrentRowData] = useState<AvailableStation | null>(null);
  const [sidePanelOpen, setSidePanelOpen] = useState<boolean>(false);

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const { theme } = useContext(ThemeContext);

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [selectedStationId, setSelectedStationId] = useState<string | null>(null);

  const { page: pageParameter } = useParams<{ page: string }>();

  /**
   * Handle the fetch the data function
   * @param data
   */
  const fetchData = (data: RequestStationInterface) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchApprovedStations({
          ...data,
        }),
      );
    }
  };

  const column = [
    {
      label: t('stationName'),
      accessorKey: 'name' as keyof AvailableStation,
      sortAllow: true,
    },
    {
      label: t('landDocument'),
      accessorKey: 'landOwnerShipDocument' as keyof AvailableStation,
      hideWhenSidePanelOpen: true,
      /**
       * Document view in table
       * @param station
       * @param index
       */
      customData: (station: AvailableStation) =>
        station?.landOwnerShipDocument?.length ? (
          <div className="document-in-station-table">
            <div className="document-container">
              <span>{theme === 'light' ? <DocumentIcon /> : <DocumentIconDark />}</span>
              <p className="table-row-each-data">{station?.landOwnerShipDocument?.[0]?.fileName}</p>
            </div>
            {station?.landOwnerShipDocument?.length > 1 && (
              <div className="document-count-wrap">
                <p className="table-row-each-data">+{station.landOwnerShipDocument.length - 1}</p>
              </div>
            )}
          </div>
        ) : null,
    },
    {
      label: t('ebReports'),
      accessorKey: 'ebReport' as keyof AvailableStation,
      hideWhenSidePanelOpen: true,
      /**
       * Document view in table
       * @param station
       * @param index
       */
      customData: (station: AvailableStation) =>
        station?.ebReport?.length ? (
          <div className="document-in-station-table">
            <div className="document-container">
              <span>{theme === 'light' ? <DocumentIcon /> : <DocumentIconDark />}</span>
              <p className="table-row-each-data">{station?.ebReport?.[0]?.fileName}</p>
            </div>
            {station?.ebReport?.length > 1 && (
              <div className="document-count-wrap">
                <p className="table-row-each-data">+{station.ebReport.length - 1}</p>
              </div>
            )}
          </div>
        ) : null,
    },
    {
      label: t('electricityType'),
      accessorKey: 'electricityContractType' as keyof AvailableStation,
      sortAllow: false,
    },
    {
      label: t('operatingHrs'),
      accessorKey: 'opensAt' as keyof AvailableStation,
      sortAllow: true,
      /**
       * Time view in table
       * @param station
       * @param index
       */
      customData: (station: AvailableStation) =>
        station.opensAt && station.closesAt ? (
          <p className="table-row-each-data">{`${FormatUtcString(station.opensAt)} - ${FormatUtcString(station.closesAt)}`}</p>
        ) : null,
    },
    {
      label: t('location'),
      accessorKey: 'address' as keyof AvailableStation,
      sortAllow: false,
      /**
       * Location view in table
       * @param station
       * @param index
       */
      customData: (station: AvailableStation) =>
        station.address ? (
          <div className="location-in-station-table">
            <span>
              <LocationIcon />
            </span>
            <p className="table-row-each-data">{station.address}</p>
          </div>
        ) : null,
    },
    {
      label: t('media'),
      accessorKey: 'medias' as keyof AvailableStation,
      hideWhenSidePanelOpen: true,
      /**
       * Media view in table
       * @param station
       * @param index
       */
      customData: (station: AvailableStation) =>
        station.medias ? (
          <div className="medias-in-station-table">
            {(() => {
              let selectedFilesView;
              if (station.medias && station.medias?.length <= 4) {
                selectedFilesView = (
                  <div className="selected-files-view-wrap">
                    {station.medias?.map((media) => (
                      <div className="single-selected-wrap">
                        {(() => {
                          let fileTypeView;
                          if (media?.fileType?.includes('image')) {
                            fileTypeView = (
                              <img src={media.signedUrl} alt="" className="selected-image" />
                            );
                          } else if (media?.fileType?.includes('video')) {
                            fileTypeView = (
                              <video controls={false} className="selected-video" preload="none">
                                <source src={media.signedUrl} type="video/mp4" />
                                <track
                                  src="path/to/captions.vtt"
                                  kind="captions"
                                  label={t('englishCaptions')}
                                  default
                                />
                                {t('browserNoSupportVideo')}
                              </video>
                            );
                          }
                          return fileTypeView;
                        })()}
                      </div>
                    ))}
                  </div>
                );
              } else if (station.medias && station.medias?.length > 4) {
                selectedFilesView = (
                  <div className="selected-files-view-wrap">
                    {station.medias.slice(0, 3).map((media) => (
                      <div className="single-selected-wrap">
                        {(() => {
                          let fileTypeView;
                          if (media?.fileType?.includes('image')) {
                            fileTypeView = (
                              <img src={media.signedUrl} alt="" className="selected-image" />
                            );
                          } else if (media?.fileType?.includes('video')) {
                            fileTypeView = (
                              <video controls={false} className="selected-video" preload="none">
                                <source src={media.signedUrl} type="video/mp4" />
                                <track
                                  src="path/to/captions.vtt"
                                  kind="captions"
                                  label={t('englishCaptions')}
                                  default
                                />
                                {t('browserNoSupportVideo')}
                              </video>
                            );
                          }
                          return fileTypeView;
                        })()}
                      </div>
                    ))}
                    <div className="single-selected-wrap">
                      <p className="number-of-files">+{station.medias.length - 3}</p>
                    </div>
                  </div>
                );
              }
              return selectedFilesView;
            })()}
          </div>
        ) : null,
    },
    {
      label: t('availability'),
      accessorKey: 'isAvailable' as keyof AvailableStation,
      customData: renderAvailability,
    },
  ];

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setApprovedStationFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      page: pageNumber,
      limit,
    });
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
      sortBy,
      sortField,
    });
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('stationName'), accessorKey: 'name' },
    { label: t('electricityType'), accessorKey: 'electricityContractType' },
    { label: t('operatingHrs'), accessorKey: 'opensAt' },
    { label: t('location'), accessorKey: 'address' },
    { label: t('availability'), accessorKey: 'isAvailable' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    stationApi
      .fetchStationsExport(
        removeEmptyKeys({
          ...queryParameters,
          submissionStatus: 'approved',
          sortBy,
          sortField,
        }),
      )
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.results.map((row: AvailableStation, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'opensAt':
                return `${FormatUtcString(row.opensAt)} - ${FormatUtcString(row.closesAt)}`;
              case 'isAvailable':
                return row.isAvailable ? t('available') : t('unavailable');
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'approved-station-list.csv');
          } else {
            exportPDF(headers, rows, 'approved-station-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  /**
   * Add account function
   */
  const addStation = () => {
    const parameters = new URLSearchParams(searchParameters);
    parameters.delete('stationId'); // close row panel if open
    setSearchParameters(parameters);

    setSidePanelOpen(true);
  };

  /**
   * Handle the open side panel on row click function
   * @param row - The facility tag data
   */
  const handleOpenSidePanelOnRowClick = (_: string, row: AvailableStation) => {
    if (!row?._id) return;

    const parameters = new URLSearchParams(searchParameters);

    parameters.set('stationId', row._id);
    setSearchParameters(parameters);

    setSidePanelOpen(false); // close add panel
    setCurrentRowData(row);
    setIsEditMode(false);
  };

  /**
   * Function to expand the facility
   */
  const expandFunction = (type?: string) => {
    if (!currentRowData) return;

    const accountCustomId = currentRowData?._id;
    const tenantId = currentRowData?.name;

    if (!accountCustomId || !tenantId) return;

    if (type === 'new') {
      const url = `/ev-admin/station-management/approved/station/${currentRowData._id}/station-dashboard?searchBy=${currentRowData.name}`;
      window.open(url, '_blank');
    } else {
      navigate({
        pathname: `/ev-admin/station-management/approved/station/${currentRowData._id}/station-dashboard`,
        search: `?searchBy=${currentRowData.name}`,
      });
    }
  };

  /**
   * Handle delete click
   */
  const handleDeleteClick = (stationId: string) => {
    setSelectedStationId(stationId);
    setDeleteModalOpen(true);
  };

  /**
   * Handle confirm delete
   */
  const handleConfirmDelete = async () => {
    if (selectedStationId) {
      try {
        await stationApi.deleteStation(selectedStationId);
        dispatch(
          setNotificationDetails({
            type: 'success',
            notificationDetails: {
              statusCode: 200,
              message: t('stationDeletedSuccessfully'),
            },
          }),
        );
        handleRefresh();
        setDeleteModalOpen(false);
        // Close side panel
        const parameters = new URLSearchParams(searchParameters);
        parameters.delete('stationId');
        setSearchParameters(parameters);
      } catch (error: any) {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      }
    }
  };

  useEffect(() => {
    if (isFilterUpdated || stations.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);

      fetchData({
        ...queryParameters,
        page: DefaultApiLimit.page,
        limit: DefaultApiLimit.limit,
      });

      dispatch(disableFiltersApplied());
    }
  }, [currentFacilityTagsList, filters]);

  useEffect(() => {
    const hasSidePanelParameter = searchParameters.get('stationId');

    if (!hasSidePanelParameter) {
      dispatch(
        setPageTitle({
          title: t('chargingStations'),
          icon: <StationIcon />,
          count: totalResults,
        }),
      );
    } else {
      dispatch(
        setPageTitle({
          title: t('chargingStations'),
          icon: <StationIcon />,
          count: undefined,
        }),
      );
    }
  }, [totalResults, searchParameters, t]);

  useEffect(() => {
    if (pageParameter === 'add') {
      setSidePanelOpen(true);
    }
  }, [pageParameter]);

  return (
    <>
      <ModalBox
        title={t('deleteStation')}
        header={false}
        open={deleteModalOpen}
        closeModal={() => setDeleteModalOpen(false)}
        content={
          <DeleteWarning
            title={t('deleteStation')}
            description={t('deleteStationWarning')}
            cancelButtonOnClick={() => setDeleteModalOpen(false)}
            submitButtonOnClick={handleConfirmDelete}
            selectedRowId={selectedStationId}
          />
        }
      />
      <CustomTable
        dataKey="StationApproved"
        paginate
        loading={loading}
        limit={limit}
        header={column}
        data={stations}
        currentPage={page}
        sortData={{
          sortBy,
          sortField,
        }}
        handleSortChange={handleSortFunction}
        totalPages={totalPages}
        totalResults={totalResults}
        handlePageChange={handlePageChange}
        handleLimitChange={handleLimitChange}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        exportData={stations}
        topHeader={false}
        filters={filters}
        filtersToDispatch={updateStationFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchListFilters}
        refreshRequired
        refreshCurrentPages={handleRefresh}
        hoverLabel={t('refresh')}
        serialNo
        flexDirection
        onHiddenColumnsChange={setHiddenColumns}
        rowClickEvent={handleOpenSidePanelOnRowClick}
        rowClickParamsKey={searchParameters.get('deviceId') ? 'deviceId' : 'stationId'}
        rowClickSidePanel={
          searchParameters.get('deviceId') ? (
            <DeviceSidePanel fetchIdFrom="queryParams" />
          ) : (
            <StationSidePanel
              fetchIdFrom="queryParams"
              onEditModeChange={setIsEditMode}
              onDeleteStation={handleDeleteClick}
            />
          )
        }
        sidePanelEnabled={sidePanelOpen}
        sidePanelManualContent={
          <AddStation onClose={() => setSidePanelOpen(false)} onSuccess={handleRefresh} />
        }
        onSidePanelClose={() => {
          const parameters = new URLSearchParams(searchParameters);
          parameters.delete('stationId');
          parameters.delete('deviceId');
          setSearchParameters(parameters);

          setSidePanelOpen(false);
          setIsEditMode(false);
        }}
        sidePanelTopLeftContent={
          <div className="side-panel-header-left-icons">
            {!isEditMode && !sidePanelOpen && (
              <>
                <div
                  role="presentation"
                  className="access-facility"
                  onClick={() => expandFunction()}>
                  <ExpandIcon />
                </div>
                <div
                  role="presentation"
                  className="access-facility"
                  onClick={() => expandFunction('new')}>
                  <NewTabIcon />
                </div>
              </>
            )}
          </div>
        }
        leftTopContent={
          <div className="table-bottom-button-list">
            <div className="table-button-item">
              <MyButton
                label={t('addStation')}
                buttonType="submit"
                onClickFunc={addStation}
                svgIcon={<PlusIcon />}
              />
            </div>
          </div>
        }
      />
    </>
  );
}

export default ApprovedList;
